import { supabase, LabTATMetric } from '../lib/supabase';
import { getTenantId } from '../contexts/TenantContext';

export const labTATService = {
  async getAll(): Promise<LabTATMetric[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('ordered_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<LabTATMetric | null> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();

    if (error) throw error;
    return data;
  },

  async getByOrderId(orderId: string): Promise<LabTATMetric | null> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('order_id', orderId)
      .single();

    if (error) throw error;
    return data;
  },

  async create(tatMetric: Omit<LabTATMetric, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<LabTATMetric> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .insert({ ...tatMetric, tenant_id: tenantId })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, tatMetric: Partial<LabTATMetric>): Promise<LabTATMetric> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .update(tatMetric)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getDashboardMetrics(dateRange?: { start: string; end: string }) {
    const tenantId = getTenantId();
    let query = supabase
      .from('lab_tat_metrics')
      .select('*')
      .eq('tenant_id', tenantId);

    if (dateRange) {
      query = query
        .gte('ordered_at', dateRange.start)
        .lte('ordered_at', dateRange.end);
    }

    const { data, error } = await query;
    if (error) throw error;

    const metrics = data || [];
    
    return {
      totalOrders: metrics.length,
      withinTarget: metrics.filter(m => m.tat_status === 'within_target').length,
      nearBreach: metrics.filter(m => m.tat_status === 'near_breach').length,
      breached: metrics.filter(m => m.tat_status === 'breached').length,
      averageTAT: metrics.reduce((sum, m) => sum + (m.total_tat || 0), 0) / metrics.length || 0,
      departmentBreakdown: this.groupByDepartment(metrics),
      priorityBreakdown: this.groupByPriority(metrics)
    };
  },

  async getBreachedOrders(): Promise<LabTATMetric[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_tat_metrics')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('tat_status', 'breached')
      .order('total_tat', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  groupByDepartment(metrics: LabTATMetric[]) {
    const grouped = metrics.reduce((acc, metric) => {
      const dept = metric.department || 'Unknown';
      if (!acc[dept]) {
        acc[dept] = { total: 0, withinTarget: 0, breached: 0 };
      }
      acc[dept].total++;
      if (metric.tat_status === 'within_target') acc[dept].withinTarget++;
      if (metric.tat_status === 'breached') acc[dept].breached++;
      return acc;
    }, {} as Record<string, any>);

    return Object.entries(grouped).map(([department, stats]) => ({
      department,
      ...stats,
      breachRate: ((stats as any).breached / (stats as any).total * 100).toFixed(1)
    }));
  },

  groupByPriority(metrics: LabTATMetric[]) {
    const grouped = metrics.reduce((acc, metric) => {
      const priority = metric.priority || 'routine';
      if (!acc[priority]) {
        acc[priority] = { total: 0, withinTarget: 0, breached: 0 };
      }
      acc[priority].total++;
      if (metric.tat_status === 'within_target') acc[priority].withinTarget++;
      if (metric.tat_status === 'breached') acc[priority].breached++;
      return acc;
    }, {} as Record<string, any>);

    return Object.entries(grouped).map(([priority, stats]) => ({
      priority,
      ...stats,
      breachRate: ((stats as any).breached / (stats as any).total * 100).toFixed(1)
    }));
  }
};
